import React, { useRef } from 'react';
import { CheckCircle2, Circle, Download, ExternalLink, Upload } from 'lucide-react';
import type { DocumentSlotDefinition } from './documentSlots';
import { getToken } from '../../authentication/authProvider';
import { fallbackFilename, saveResponse } from '../../lib/download';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

interface DocumentSlotRowProps {
  slot: DocumentSlotDefinition;
  document?: {
    id?: string;
    public_url?: string | null;
    storage_path?: string;
    received_at?: string;
    original_filename?: string;
    /** Partner-portal uploads (T-108): "Brampton VC". */
    uploaded_by?: string | null;
  } | null;
  onUpload?: (file: File) => void;
  onIssue?: () => void;
  /** Renders a "Generate" button when the document is absent (e.g. the receipt). */
  onGenerate?: () => void;
  generating?: boolean;
  uploading?: boolean;
  extraActions?: React.ReactNode;
  /** Shown only while the document is missing (e.g. "Add on Details", T-212). */
  missingActions?: React.ReactNode;
  // Override the default "Received"/"Missing" badge — used by sections like
  // Benefits where the document has more states (Unsigned vs Signed).
  badge?: { label: string; tone: 'green' | 'amber' | 'gray' };
}

const TONE_CLASSES: Record<'green' | 'amber' | 'gray', string> = {
  green: 'bg-green-100 text-green-700',
  amber: 'bg-amber-100 text-amber-700',
  gray: 'bg-gray-100 text-gray-500',
};

export function DocumentSlotRow({ slot, document, onUpload, onIssue, onGenerate, generating, uploading, extraActions, missingActions, badge }: DocumentSlotRowProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const received = !!document;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onUpload) {
      onUpload(file);
    }
    // Reset so same file can be re-selected
    e.target.value = '';
  };

  const handleView = async () => {
    if (!document?.id) return;
    try {
      const token = await getToken();
      const res = await fetch(`${PAPERMAN_API_URL}documents/funeral-document/${document.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        window.open(URL.createObjectURL(blob), '_blank');
      }
    } catch (e) {
      console.error('Failed to view document', e);
    }
  };

  const handleDownload = async () => {
    if (!document?.id) return;
    try {
      const token = await getToken();
      const res = await fetch(`${PAPERMAN_API_URL}documents/funeral-document/${document.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // The backend names it ("Cleo - SP-3 - Jean Tremblay.pdf"); the stored
      // name is only the fallback (T-173).
      if (res.ok) await saveResponse(res, fallbackFilename(document));
    } catch (e) {
      console.error('Failed to download document', e);
    }
  };

  const isUploadable = slot.source === 'upload';
  const isVerified = slot.source === 'verified';

  // Actions live on their own row BELOW the title (like the POD cards) so the
  // title can wrap and the action cluster never forces the panel wider than the
  // sidebar. Compute up front so we only render the row when it has content.
  const hasActions = received
    ? !!document?.id || (isUploadable && !!onUpload) || !!extraActions
    : (isUploadable && !!onUpload) || (isVerified && !!onIssue) || !!onGenerate || !!missingActions;

  return (
    // Not a drop target. A file dropped here bubbles up to the Documents tab,
    // which asks what it is. When this row took drops itself, the same drop
    // ALSO reached the tab, so a file meant for "Other" was silently filed into
    // this slot too, replacing the SP3 or preneed confirmation (T-050).
    <div className="rounded-md border px-3 py-2">
      {/* Title (wraps) on the left, status badge pinned top-right */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          {received ? (
            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <Circle className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium text-foreground break-words">
              {slot.label}
            </div>
            {received && document?.received_at && (
              <div className="text-[11px] text-muted-foreground">
                {new Date(document.received_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
                {document.uploaded_by ? ` · Uploaded by ${document.uploaded_by}` : ''}
              </div>
            )}
          </div>
        </div>

        <span
          className={`text-[10px] font-medium px-1.5 py-0.5 rounded flex-shrink-0 ${
            badge ? TONE_CLASSES[badge.tone] : received ? TONE_CLASSES.green : TONE_CLASSES.gray
          }`}
        >
          {badge?.label || (received ? 'Received' : 'Missing')}
        </span>
      </div>

      {hasActions && (
        <div className="flex items-center gap-1 flex-wrap mt-1.5">
          {received ? (
            <>
              {document?.id && (
                <>
                  <button
                    onClick={handleView}
                    className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                    title="View document"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={handleDownload}
                    className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                    title="Download"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
              {isUploadable && onUpload && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground disabled:opacity-50"
                    title="Replace document"
                  >
                    <Upload className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
              {extraActions}
            </>
          ) : (
            <>
              {isUploadable && onUpload && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground disabled:opacity-50"
                    title="Upload file"
                  >
                    <Upload className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
              {isVerified && onIssue && (
                <button
                  onClick={onIssue}
                  className="text-[11px] text-blue-600 hover:text-blue-800"
                >
                  Issue
                </button>
              )}
              {onGenerate && (
                <button
                  onClick={onGenerate}
                  disabled={generating}
                  className="text-[11px] text-blue-600 hover:text-blue-800 disabled:opacity-50"
                >
                  {generating ? 'Generating…' : 'Generate'}
                </button>
              )}
              {missingActions}
            </>
          )}
        </div>
      )}
    </div>
  );
}
